import { config, isRegistrationOpen } from '@/lib/config';
import {
    createRouter,
    createWebHistory,
    type RouteRecordRaw,
} from 'vue-router';
import { abilityGuard, authGuard, guestGuard, roleGuard } from './guards';

/**
 * Route meta interface for type-safe route configuration.
 */
declare module 'vue-router' {
    interface RouteMeta {
        /** Requires authentication */
        requiresAuth?: boolean;
        /** Requires guest (not authenticated) */
        requiresGuest?: boolean;
        /** Required abilities (any of) */
        abilities?: string[];
        /** Required roles (any of) */
        roles?: string[];
        /** Page title */
        title?: string;
        /** Layout component name */
        layout?: 'default' | 'auth' | 'admin' | 'blank';
    }
}

/**
 * Route definitions.
 */
const routes: RouteRecordRaw[] = [
    // Public routes
    {
        path: '/',
        name: 'home',
        component: () => import('@/pages/Welcome.vue'),
        meta: { title: 'Welcome', layout: 'blank' },
    },

    // Guest routes (must not be authenticated)
    {
        path: '/login',
        name: 'login',
        component: () => import('@/pages/auth/Login.vue'),
        meta: { requiresGuest: true, title: 'Login', layout: 'auth' },
    },
    {
        path: '/register',
        name: 'register',
        component: () => import('@/pages/auth/Register.vue'),
        meta: { requiresGuest: true, title: 'Register', layout: 'auth' },
        beforeEnter: () => {
            if (!isRegistrationOpen) {
                return { name: 'login' };
            }
            return true;
        },
    },
    {
        path: '/register/invite/:token',
        name: 'register.invite',
        component: () => import('@/pages/auth/InviteRegister.vue'),
        meta: {
            requiresGuest: true,
            title: 'Accept Invitation',
            layout: 'auth',
        },
    },
    {
        path: '/account-pending',
        name: 'account-pending',
        component: () => import('@/pages/auth/AccountPending.vue'),
        meta: { requiresGuest: true, title: 'Account Pending', layout: 'auth' },
    },
    {
        path: '/forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/auth/ForgotPassword.vue'),
        meta: { requiresGuest: true, title: 'Forgot Password', layout: 'auth' },
    },
    {
        path: '/reset-password/:token',
        name: 'reset-password',
        component: () => import('@/pages/auth/ResetPassword.vue'),
        meta: { requiresGuest: true, title: 'Reset Password', layout: 'auth' },
    },
    {
        path: '/two-factor-challenge',
        name: 'two-factor-challenge',
        component: () => import('@/pages/auth/TwoFactorChallenge.vue'),
        meta: {
            requiresGuest: true,
            title: 'Two-Factor Authentication',
            layout: 'auth',
        },
    },

    // Protected routes (must be authenticated)
    {
        path: '/dashboard',
        name: 'dashboard',
        component: () => import('@/pages/Dashboard.vue'),
        meta: { requiresAuth: true, title: 'Dashboard', layout: 'default' },
    },
    {
        path: '/verify-email',
        name: 'verify-email',
        component: () => import('@/pages/auth/VerifyEmail.vue'),
        meta: { requiresAuth: true, title: 'Verify Email', layout: 'default' },
    },
    {
        path: '/confirm-password',
        name: 'confirm-password',
        component: () => import('@/pages/auth/ConfirmPassword.vue'),
        meta: {
            requiresAuth: true,
            title: 'Confirm Password',
            layout: 'default',
        },
    },

    // Settings routes
    {
        path: '/settings',
        meta: { requiresAuth: true, layout: 'default' },
        children: [
            {
                path: '',
                redirect: '/settings/profile',
            },
            {
                path: 'profile',
                name: 'settings.profile',
                component: () => import('@/pages/settings/Profile.vue'),
                meta: { title: 'Profile Settings' },
            },
            {
                path: 'password',
                name: 'settings.password',
                component: () => import('@/pages/settings/Password.vue'),
                meta: { title: 'Password Settings' },
            },
            {
                path: 'appearance',
                name: 'settings.appearance',
                component: () => import('@/pages/settings/Appearance.vue'),
                meta: { title: 'Appearance Settings' },
            },
            {
                path: 'two-factor',
                name: 'settings.two-factor',
                component: () => import('@/pages/settings/TwoFactor.vue'),
                meta: { title: 'Two-Factor Authentication' },
            },
        ],
    },

    // Admin routes
    {
        path: '/admin',
        meta: {
            requiresAuth: true,
            roles: ['admin', 'super-admin'],
            layout: 'admin',
        },
        children: [
            {
                path: '',
                name: 'admin.index',
                component: () => import('@/pages/admin/Index.vue'),
                meta: { title: 'Admin Dashboard' },
            },
            // Admin Panel (module selector + tab bar navigation)
            {
                path: 'panel/:module?/:tab?',
                name: 'admin.panel',
                component: () => import('@/pages/admin/AdminPanel.vue'),
                meta: { title: 'Admin Panel' },
            },
            // User management
            {
                path: 'users',
                name: 'admin.users.index',
                component: () => import('@/pages/admin/users/Index.vue'),
                meta: { title: 'User Management', abilities: ['user:view'] },
            },
            {
                path: 'users/invitations',
                name: 'admin.users.invitations',
                component: () => import('@/pages/admin/users/Invitations.vue'),
                meta: { title: 'Invitations', abilities: ['user:invite'] },
            },
            {
                path: 'users/create',
                name: 'admin.users.create',
                component: () => import('@/pages/admin/users/Create.vue'),
                meta: { title: 'Create User', abilities: ['user:create'] },
            },
            {
                path: 'users/:id',
                name: 'admin.users.show',
                component: () => import('@/pages/admin/users/Show.vue'),
                meta: { title: 'View User', abilities: ['user:view'] },
            },
            {
                path: 'users/:id/edit',
                name: 'admin.users.edit',
                component: () => import('@/pages/admin/users/Edit.vue'),
                meta: { title: 'Edit User', abilities: ['user:update'] },
            },
            // Role management
            {
                path: 'roles',
                name: 'admin.roles.index',
                component: () => import('@/pages/admin/roles/Index.vue'),
                meta: { title: 'Role Management', abilities: ['role:view'] },
            },
            {
                path: 'roles/create',
                name: 'admin.roles.create',
                component: () => import('@/pages/admin/roles/Create.vue'),
                meta: { title: 'Create Role', abilities: ['role:create'] },
            },
            {
                path: 'roles/:id/edit',
                name: 'admin.roles.edit',
                component: () => import('@/pages/admin/roles/Edit.vue'),
                meta: { title: 'Edit Role', abilities: ['role:update'] },
            },
            // Audit logs
            {
                path: 'audit-logs',
                name: 'admin.audit-logs.index',
                component: () => import('@/pages/admin/audit-logs/Index.vue'),
                meta: { title: 'Audit Logs', abilities: ['audit:view'] },
            },
            // System settings
            {
                path: 'settings',
                name: 'admin.settings.index',
                component: () => import('@/pages/admin/settings/Index.vue'),
                meta: {
                    title: 'System Settings',
                    abilities: ['system:settings'],
                },
            },
        ],
    },

    // HR routes
    {
        path: '/hr',
        meta: {
            requiresAuth: true,
            roles: ['admin', 'super-admin'],
            layout: 'default',
        },
        children: [
            {
                path: '',
                redirect: '/hr/panel',
            },
            // HR Panel (module selector + tab bar navigation)
            {
                path: 'panel/:module?/:tab?',
                name: 'hr.panel',
                component: () => import('@/pages/hr/HrPanel.vue'),
                meta: { title: 'HR Panel' },
            },
        ],
    },

    // Booking routes — single-module panel; tabs route directly under
    // /booking (no /panel/<module> nesting). BookingPanel.vue resolves
    // the active tab from the URL's last segment.
    {
        path: '/booking',
        meta: {
            requiresAuth: true,
            abilities: ['booking.view'],
            layout: 'default',
        },
        children: [
            {
                path: '',
                redirect: '/booking/list',
            },
            {
                path: 'list',
                name: 'booking.list',
                component: () => import('@/pages/booking/BookingPanel.vue'),
                meta: { title: 'Bookings' },
            },
            {
                path: 'schedule',
                name: 'booking.schedule',
                component: () => import('@/pages/booking/BookingPanel.vue'),
                meta: { title: 'Schedule' },
            },
            {
                path: 'invoices',
                name: 'booking.invoices',
                component: () => import('@/pages/booking/BookingPanel.vue'),
                meta: { title: 'Invoices' },
            },
            {
                path: 'create',
                name: 'booking.create',
                component: () => import('@/pages/booking/Create.vue'),
                meta: {
                    title: 'Create Booking',
                    abilities: ['booking.create'],
                },
            },
            {
                path: ':id',
                name: 'booking.show',
                component: () => import('@/pages/booking/Show.vue'),
                meta: { title: 'View Booking' },
            },
            {
                path: ':id/edit',
                name: 'booking.edit',
                component: () => import('@/pages/booking/Edit.vue'),
                meta: { title: 'Edit Booking', abilities: ['booking.update'] },
            },
        ],
    },

    // 404 catch-all
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/errors/NotFound.vue'),
        meta: { title: 'Not Found', layout: 'blank' },
    },
];

/**
 * Create and configure the router.
 */
const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { el: to.hash };
        }
        return { top: 0 };
    },
});

// Register navigation guards
router.beforeEach(authGuard);
router.beforeEach(guestGuard);
router.beforeEach(roleGuard);
router.beforeEach(abilityGuard);

// Update document title
router.afterEach((to) => {
    const appName = config.appName;
    const title = to.meta.title ? `${to.meta.title} - ${appName}` : appName;
    document.title = title;
});

export { router };
export default router;
